/*
 * PRD-037 — Modal de contratación del paso de plan del claim (app SEPARADA).
 *
 * Espeja la lógica de @pages/Subscriptionsv2/components/ActivatePlanModal (select → card; FR-28
 * tarjeta|transferencia) pero SIN su acoplamiento a Redux: aquel usa useActivatePlan (useSelector +
 * useAuth), que no funciona en el portal del partner. Aquí orquestamos los MISMOS componentes
 * presentacionales reusados —PaymentMethodSelector + StripePaymentForm— sobre convertFreemiumToPlan
 * (axiosInstance directo). Los errores se muestran inline (el partner no monta <Toaster>).
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@plick/ui';
import { V4 } from '@components/v4/tokens';
import { Button } from '@components/v4/Button/Button';
import { PaymentMethodSelector } from '@pages/Subscriptionsv2/components/PaymentMethodSelector';
import { StripePaymentForm, isStripeConfigured } from '@pages/Subscriptionsv2/components/StripePaymentForm';
import type { MetodoPago } from '@pages/Subscriptionsv2/hooks/useActivatePlan';
import type { AvailableSubscription } from '@interfaces/general';
import { formatToCurrency } from '@utils/formats';
import { convertFreemiumToPlan } from './useClaimPlans';

// FR-28: el default contractual del método de pago es 'transferencia'.
const DEFAULT_METODO_PAGO: MetodoPago = 'transferencia';

interface ClaimPlanModalProps {
  plan: AvailableSubscription;
  isOpen: boolean;
  onClose: () => void;
  /** Se invoca tras una contratación exitosa (tarjeta confirmada o factura de transferencia enviada). */
  onActivated: () => void;
}

type Step = 'select' | 'card';

export const ClaimPlanModal = ({ plan, isOpen, onClose, onActivated }: ClaimPlanModalProps) => {
  const [metodoPago, setMetodoPago] = useState<MetodoPago>(DEFAULT_METODO_PAGO);
  const [step, setStep] = useState<Step>('select');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  // Al cambiar de plan, descartar el secret/paso del intento previo.
  useEffect(() => {
    setClientSecret(null);
    setStep('select');
    setError(null);
  }, [plan.id]);

  const cardConfigured = isStripeConfigured();

  const handleClose = () => {
    setStep('select');
    setClientSecret(null);
    setMetodoPago(DEFAULT_METODO_PAGO);
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    // Guard síncrono por ref: bloquea doble-click concurrente sin depender del estado async.
    if (submittingRef.current || isActivating) return;
    submittingRef.current = true;
    setError(null);
    setIsActivating(true);
    try {
      // Tarjeta: reusar el client_secret del intento previo en vez de duplicar la suscripción.
      if (metodoPago === 'tarjeta' && clientSecret) {
        setStep('card');
        return;
      }

      const response = await convertFreemiumToPlan(plan.id, metodoPago);

      if (metodoPago === 'transferencia') {
        if (response.hosted_invoice_url) {
          window.open(response.hosted_invoice_url, '_blank', 'noopener,noreferrer');
          onActivated();
        } else {
          setError('No se recibió la factura de pago. Intenta de nuevo.');
        }
        return;
      }

      if (response.client_secret) {
        setClientSecret(response.client_secret);
        setStep('card');
      } else {
        setError('No se recibió la información de pago. Intenta de nuevo.');
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'No se pudo activar el plan. Intenta de nuevo.';
      setError(message);
    } finally {
      setIsActivating(false);
      submittingRef.current = false;
    }
  };

  return (
    <Modal padding="0rem" maxHeight="90vh" maxWidth="520px" isOpen={isOpen} onClose={handleClose}>
      <div style={{ padding: 24, fontFamily: 'Poppins, sans-serif' }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: V4.primary }}>Activar {plan.nombre}</h3>
        {typeof plan.precio === 'number' && (
          <p style={{ margin: '4px 0 20px', fontSize: 13, color: V4.grayUltraDark }}>
            {formatToCurrency(plan.precio)} · {plan.periodo_pago === 'anual' ? 'pago anual' : 'pago mensual'}
          </p>
        )}

        {step === 'select' && (
          <>
            <p
              style={{
                margin: '0 0 12px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: V4.grayUltraDark,
              }}
            >
              Elige cómo quieres pagar
            </p>

            <PaymentMethodSelector
              value={metodoPago}
              onChange={(m) => {
                // Cambiar de método descarta el secret del intento previo.
                if (m !== metodoPago) setClientSecret(null);
                setMetodoPago(m);
              }}
              disabled={isActivating}
            />

            {metodoPago === 'tarjeta' && !cardConfigured && (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: V4.warningDark, lineHeight: 1.4 }}>
                El pago con tarjeta no está disponible por ahora. Selecciona transferencia bancaria para continuar.
              </p>
            )}

            {error && (
              <p style={{ margin: '12px 0 0', fontSize: 12, color: V4.danger, lineHeight: 1.4 }} role="alert">
                {error}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <Button variant="secondary" onClick={handleClose} disabled={isActivating}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={isActivating}
                disabled={metodoPago === 'tarjeta' && !cardConfigured}
              >
                Continuar
              </Button>
            </div>
          </>
        )}

        {step === 'card' && clientSecret && (
          <StripePaymentForm clientSecret={clientSecret} onSuccess={onActivated} onCancel={() => setStep('select')} />
        )}
      </div>
    </Modal>
  );
};
